import React from 'react';
import {
    Box,
    Stepper as MuiStepper,
    Step,
    StepLabel,
    StepContent,
    Typography,
    IconButton,
    Divider,
    CircularProgress,
} from '@mui/material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { StepIcon } from '@tymely/features/Ticket/QaMode/style';
import CheckIcon from '@mui/icons-material/Check';
import { useQaStepperContext } from '@tymely/features/Ticket/QaMode/QaStepperContext';
import { QaIntents } from '@tymely/features/Ticket/QaMode/QaIntents';
import QaArgumentList from '@tymely/features/Ticket/QaMode/QaArguments/QaArgumentList';
import { QaResolutionStep } from '@tymely/features/Ticket/QaMode/QaResolutionStep/QaResolutionStep';
import { STEP_ARGUMENTS, STEP_INTENT, STEP_RESOLUTION } from '@tymely/features/Ticket/QaMode/constants';
import { selectedPolicySetIdAtom } from '@tymely/atoms';
import { useAtomValue } from 'jotai';

const steps = [
    {
        label: 'Intent',
        content: () => <QaIntents />,
        subtitle: 'if modified, skip directly to Resolution',
    },
    {
        label: 'Arguments',
        content: () => <QaArgumentList />,
        subtitle: 'if modified, skip directly to Resolution',
    },
    {
        label: 'Resolution',
        content: () => <QaResolutionStep />,
    },
];

const QaStepper = () => {
    const {
        activeStep,
        isIntentChanged,
        hasOneRejectedArg,
        setActiveStep,
        isArgQueryLoading,
        intentArg,
        approvedArgs,
        textArgs,
    } = useQaStepperContext();
    const selectedPolicySetId = useAtomValue(selectedPolicySetIdAtom);

    const backgroundColor = (step: number) => {
        if (step === STEP_INTENT) {
            return isIntentChanged ? 'warning.main' : 'success';
        } else if (step === STEP_ARGUMENTS) {
            if (!intentArg?.qa_at) {
                return 'text.disabled';
            }
            return hasOneRejectedArg ? 'warning.main' : 'success';
        } else if (step === STEP_RESOLUTION) {
            if (approvedArgs.size !== textArgs?.length && !hasOneRejectedArg && !isIntentChanged) {
                return 'text.disabled';
            } else {
                return 'success';
            }
        }
        return 'success';
    };

    const onStepChange = (index: number) => {
        if (index === activeStep) {
            setActiveStep(-1);
        } else if (index === STEP_ARGUMENTS) {
            if (intentArg?.qa_at || !intentArg) {
                setActiveStep(index);
            }
        } else if (index === STEP_RESOLUTION) {
            if (approvedArgs.size === textArgs?.length || hasOneRejectedArg) {
                setActiveStep(index);
            }
        } else if (index === STEP_INTENT) {
            if (intentArg || selectedPolicySetId) {
                setActiveStep(index);
            }
        }
    };
    return (
        <Box sx={{ width: '100%', height: '100%', padding: '0 20px', overflow: 'auto', position: 'relative' }}>
            <Typography variant="h5" align="left" p={1}>
                QA
            </Typography>
            <Divider />
            {isArgQueryLoading && (
                <CircularProgress
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        zIndex: 1,
                    }}
                />
            )}
            <MuiStepper activeStep={activeStep} orientation="vertical" sx={{ opacity: isArgQueryLoading ? 0.5 : 1 }}>
                {steps.map((step, index) => (
                    <Step key={step.label}>
                        <StepLabel
                            icon={
                                <StepIcon
                                    sx={{
                                        backgroundColor: backgroundColor(index),
                                    }}
                                >
                                    {activeStep > index ? <CheckIcon /> : index + 1}
                                </StepIcon>
                            }
                        >
                            <Box display="flex" alignItems="center" justifyContent="space-between">
                                <Typography>{step.label}</Typography>
                                <Typography variant="body2" color="lightgray">
                                    {step.subtitle}
                                </Typography>
                                <IconButton onClick={() => onStepChange(index)} size="small">
                                    {activeStep === index ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                </IconButton>
                            </Box>
                        </StepLabel>
                        <StepContent>{step.content()}</StepContent>
                    </Step>
                ))}
            </MuiStepper>
        </Box>
    );
};

export default QaStepper;
